import { IconChevronRight } from '@/icons';
import { cn } from '@/helpers/classnames.ts';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import Button from '../core/Button';
import { FC, ReactNode } from 'react';
import { DepartmentTreeNodeDTO } from '@/api/trafikportalen';
import IconButton from '../core/IconButton';
import { SelectedDepartment } from '@/types/selectedDepartment';

interface Props {
  departmentNode: DepartmentTreeNodeDTO;
  selectedDepartmentId: string;
  expandedDepartments: Set<string>;
  disabledDepartments?: string[];
  enabledDepartments?: string[];
  level?: number;
  onSelect: (department: SelectedDepartment) => void;
  onToggle: (department: SelectedDepartment) => void;
  icons?: Record<string, ReactNode>;
  dontHighlightSelectedDepartment?: boolean;
}

export const DepartmentTreeNode: FC<Props> = ({
  departmentNode,
  selectedDepartmentId,
  expandedDepartments,
  disabledDepartments,
  enabledDepartments,
  level = 0,
  onSelect,
  onToggle,
  icons,
  dontHighlightSelectedDepartment,
}) => {
  const { id, children, name, type } = departmentNode;

  const isSelected =
    id === selectedDepartmentId && !dontHighlightSelectedDepartment;
  const isExpanded = expandedDepartments.has(id);
  const isDisabled =
    disabledDepartments?.includes(id) ||
    (!!enabledDepartments && !enabledDepartments.includes(id));
  const [ref] = useAutoAnimateConditional<HTMLLIElement>();

  // Remove this to show school departments
  if (type.startsWith('school-')) {
    return null;
  }

  return (
    <li
      title={name}
      className={cn(
        'flex w-full flex-col gap-3',
        children.length === 0 && level > 0 && 'pl-9',
      )}
      ref={ref}
    >
      <div className="flex flex-row items-center">
        {children.length > 0 && level > 0 && (
          <IconButton
            aria-label={
              isExpanded
                ? `Dölj underavdelningar för ${name}`
                : `Visa underavdelningar för ${name}`
            }
            icon={
              <IconChevronRight
                className={cn(
                  'transition-all',
                  isExpanded && 'rotate-90 transform',
                )}
              />
            }
            variant="ghost"
            onClick={() => onToggle({ id, name })}
          />
        )}
        <Button
          className="relative w-full justify-start truncate font-normal transition-all"
          variant={isSelected ? 'filled' : 'outline'}
          onClick={() => onSelect({ id, name })}
          disabled={isDisabled}
        >
          <p className="mr-auto truncate">{name}</p>
          <div className="min-w-max">{icons?.[id]}</div>
        </Button>
      </div>
      {children.length > 0 && isExpanded && (
        <ul className={cn('flex flex-col gap-3', level > 0 && 'pl-6')}>
          {children.map((child) => (
            <DepartmentTreeNode
              departmentNode={child}
              selectedDepartmentId={selectedDepartmentId}
              expandedDepartments={expandedDepartments}
              onSelect={onSelect}
              onToggle={onToggle}
              key={child.id}
              level={level + 1}
              disabledDepartments={disabledDepartments}
              enabledDepartments={enabledDepartments}
              icons={icons}
              dontHighlightSelectedDepartment={dontHighlightSelectedDepartment}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
